/**
 * Zentrale Pfad-Helfer fuer den vereinheitlichten Vorgang (Arbeitspaket F0,
 * docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md Teil B2). Antoines Entscheidung D3 (12.09.2026):
 * ein Fall hat unter `/vorgaenge/:id` genau eine Adresse, unabhaengig von der Phase (Anfrage vor
 * Zusage, Buchung danach). `/veranstaltungen/...` verschwindet aus dem Backoffice — Ausnahme s.u.
 *
 * Jede neue Verwendung eines Vorgangs-/Buchungs-Pfads im Frontend geht ueber diese Helfer statt
 * ueber ein hartkodiertes Literal — durchgesetzt von der Ratsche `test/pfad-literale.test.ts`.
 */

/** Die eine Vorgangsseite, phasenunabhaengig (Anfrage oder Buchung). */
export const vorgangPfad = (id: string) => `/vorgaenge/${id}`;

/** Angebots-Builder zu einem Vorgang. */
export const angebotPfad = (id: string) => `/vorgaenge/${id}/angebot`;

/** Abrechnungs-Unterseite eines Vorgangs. */
export const abrechnungPfad = (id: string) => `/vorgaenge/${id}/abrechnung`;

/**
 * Die OEFFENTLICHE Angebotsseite (Vorschau, `PreviewOnlyLayout` in `App.tsx`). Bewusst WEITERHIN
 * unter `/veranstaltungen/:id/angebotsseite` — diese Adresse steht bereits in versendeten
 * Kunden-E-Mails (`apps/api/src/routes/offers.ts`) und wird NIE umgezogen (s. Kopfkommentar der
 * Route in `App.tsx` und `ui/umleitungen.tsx`).
 */
export const angebotsseitePfad = (id: string) => `/veranstaltungen/${id}/angebotsseite`;

/**
 * Posteingang, optional auf einen Vorgang vorausgewaehlt (`pages/posteingang.tsx` liest
 * `?eventId=` und oeffnet den zugehoerigen Nachrichten-Thread). Ziel der Verlaufs-Deep-Links aus
 * A4 (13.09.2026): beide Mail-Richtungen liegen dort in EINER Ansicht, waehrend `/mail-protokoll`
 * nur AUSGEHENDE Mails kennt und Inhabern vorbehalten ist (`apps/api/src/routes/system.ts`).
 */
export const posteingangPfad = (eventId?: string | null) => (eventId ? `/posteingang?eventId=${eventId}` : "/posteingang");

/** Anfragen-Liste: alle Faelle vor der Zusage. */
export const ANFRAGEN_PFAD = "/anfragen";

/** Buchungen-Liste: alle Faelle ab Zusage/Zahlung. */
export const BUCHUNGEN_PFAD = "/buchungen";

/**
 * Uebersetzt ein Alt-Ziel (`/veranstaltungen/...`), wie es weiterhin von Backend-nahem Code
 * geliefert wird (z. B. `packages/anfrage-stand#aktionBauen` — bewusst NICHT Teil von F0, s.
 * Kommentar dort), auf die neue Adresse. Fuer alle anderen Ziele (`/anfragen/...` etc.) ohne
 * Wirkung. Die App.tsx-Splat-Umleitung faengt zusaetzlich jeden direkt aufgerufenen/verlinkten
 * Alt-Pfad ab — diese Funktion vermeidet nur den unnoetigen Umweg ueber die Umleitung, wenn das
 * Ziel schon beim Navigieren bekannt ist (z. B. `pages/inquiries.tsx#fuehreAktionAus`).
 */
export function uebersetzeAltesZiel(ziel: string): string {
  if (ziel === "/veranstaltungen/:id/angebotsseite" || ziel.includes("/angebotsseite")) return ziel;
  const treffer = /^\/veranstaltungen(\/[^?]*)?(\?.*)?$/.exec(ziel);
  if (!treffer) return ziel;
  const rest = treffer[1] ?? "";
  const query = treffer[2] ?? "";
  return rest ? `/vorgaenge${rest}${query}` : `${BUCHUNGEN_PFAD}${query}`;
}

/** Reiter der Zahlungsliste (`pages/payments.tsx`, `?filter=`). */
export type ZahlungsReiter = "all" | "open" | "paid" | "failed" | "refunded";

/**
 * Zahlungsliste, optional vorgefiltert (Punkt 115, Paket 1 „Nie mehr ins Leere“):
 *  - `filter`: Reiter (fehlt bzw. „all“ = alle),
 *  - `q`: Startwert der Suche (z. B. Kundenname),
 *  - `vorgang`: nur die Zahlungen DIESES Vorgangs (sichtbarer Filter-Hinweis mit „Filter aufheben“),
 *  - `von`/`vonName`: Herkunft fuer den sichtbaren Rueckweg „← Zurück zu <vonName>“.
 */
export function zahlungenPfad(o: {
  filter?: ZahlungsReiter;
  q?: string;
  vorgang?: string;
  von?: string;
  vonName?: string;
} = {}): string {
  const p = new URLSearchParams();
  if (o.filter && o.filter !== "all") p.set("filter", o.filter);
  if (o.q) p.set("q", o.q);
  if (o.vorgang) p.set("vorgang", o.vorgang);
  if (o.von) p.set("von", o.von);
  if (o.von && o.vonName) p.set("vonName", o.vonName);
  const s = p.toString();
  return s ? `/zahlungen?${s}` : "/zahlungen";
}

/**
 * Liest einen Herkunfts-Parameter (`?von=`) sicher: nur app-interne Pfade (`/…`, nicht `//…`,
 * kein Schema) — sonst waere `?von=https://boese.example` ein offener Weiterleiter.
 */
export function sichereHerkunft(von: string | null): string | null {
  if (!von || !von.startsWith("/") || von.startsWith("//") || von.includes("\\")) return null;
  return von;
}
